import type { AbstractIntlMessages } from 'next-intl';

import { defaultLocale, type Locale } from '../index';

type ManifestEntry = {
  etag?: string;
  file: string;
  keys?: number;
  updated_at?: string;
  version?: string;
};

type Manifest = Partial<Record<Locale, ManifestEntry>>;

type MessageObject = Record<string, unknown>;

const localMessagesCache = new Map<Locale, Promise<AbstractIntlMessages>>();
const remoteMessagesCache = new Map<Locale, Promise<AbstractIntlMessages>>();

const fallbackMessages = {
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    loading: 'Loading...',
    error: 'Error',
  },
  error: {
    title: 'Error',
    message: 'An error occurred',
  },
} as AbstractIntlMessages;

function isPlainObject(value: unknown): value is MessageObject {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge(base: unknown, override: unknown): unknown {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : override;
  }

  const merged: MessageObject = { ...base };

  for (const [key, value] of Object.entries(override)) {
    if (key === '_meta') continue;

    merged[key] = deepMerge(merged[key], value);
  }

  return merged;
}

function normalizeSheetTokens(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.replace(/{{\s*([A-Za-z_][A-Za-z0-9_]*)\s*}}/g, '{$1}');
  }

  if (Array.isArray(value)) {
    return value.map(normalizeSheetTokens);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, normalizeSheetTokens(entry)])
    );
  }

  return value;
}

async function getLocalMessages(locale: Locale): Promise<AbstractIntlMessages> {
  if (!localMessagesCache.has(locale)) {
    localMessagesCache.set(locale, loadLocalMessages(locale));
  }

  return localMessagesCache.get(locale)!;
}

async function loadLocalMessages(locale: Locale): Promise<AbstractIntlMessages> {
  try {
    const mainLocaleFile = await import(`./${locale}.json`);
    return (mainLocaleFile as { default?: AbstractIntlMessages }).default || {};
  } catch (error) {
    console.warn('Could not load messages for locale %s:', locale, error);

    if (locale !== defaultLocale) {
      return getLocalMessages(defaultLocale);
    }

    return fallbackMessages;
  }
}

function resolveTranslationUrl(manifestUrl: string, file: string) {
  const manifestAbsoluteUrl = new URL(manifestUrl, window.location.origin);

  return new URL(file, manifestAbsoluteUrl).toString();
}

async function loadRemoteMessages(locale: Locale): Promise<AbstractIntlMessages> {
  const manifestUrl = process.env.NEXT_PUBLIC_I18N_MANIFEST_URL;

  if (!manifestUrl) {
    return getLocalMessages(locale);
  }

  const manifestResponse = await fetch(manifestUrl, { cache: 'no-cache' });
  if (!manifestResponse.ok) {
    throw new Error(`Failed to fetch i18n manifest: ${manifestResponse.status}`);
  }

  const manifest = (await manifestResponse.json()) as Manifest;
  const entry = manifest[locale];

  if (!entry?.file || entry.keys === 0) {
    return getLocalMessages(locale);
  }

  const translationResponse = await fetch(resolveTranslationUrl(manifestUrl, entry.file), {
    cache: 'no-cache',
  });

  if (!translationResponse.ok) {
    throw new Error(`Failed to fetch translations for ${locale}: ${translationResponse.status}`);
  }

  const [localEnglishMessages, localLocaleMessages, remoteMessages] = await Promise.all([
    getLocalMessages(defaultLocale),
    getLocalMessages(locale),
    translationResponse.json(),
  ]);

  const normalizedRemoteMessages = normalizeSheetTokens(remoteMessages);
  const localFallbackMessages = deepMerge(localEnglishMessages, localLocaleMessages);

  return deepMerge(localFallbackMessages, normalizedRemoteMessages) as AbstractIntlMessages;
}

export async function getMessages(locale: Locale): Promise<AbstractIntlMessages> {
  if (typeof window === 'undefined') {
    return getLocalMessages(locale);
  }

  if (!remoteMessagesCache.has(locale)) {
    remoteMessagesCache.set(
      locale,
      loadRemoteMessages(locale).catch(error => {
        console.warn('Could not load CDN messages for locale %s:', locale, error);
        remoteMessagesCache.delete(locale);

        return getLocalMessages(locale);
      })
    );
  }

  return remoteMessagesCache.get(locale)!;
}

export function clearMessagesCache() {
  remoteMessagesCache.clear();
}
